import { Link } from 'react-router-dom';
import { wineStock, type Wine } from '../domain/cellar';
import { onlineRatingToShow } from '../domain/online-rating';
import { latestRatedTasting } from '../domain/rating';
import { bottleCount, countryLabel, regionLabel, t, tf } from '../i18n';
import { OnlineRatingValue } from './OnlineRatingValue';
import './WineCard.css';
import './StockCard.css';

/**
 * The compact wine card of "Stock" and "À boire" (owner requests, 2026-10-09 and 2026-10-13):
 * the Domaine and vintage with the latest rating in the top right corner (the number alone, or the
 * saved "Note en ligne" with its globe), the country and region below, then the Cépage (and
 * Parcelle) with the bottles left in the bottom right corner. The colour says the wine type (the
 * due clock and alert were removed on 2026-10-15, owner). The whole card opens the wine's page.
 */
export function StockCard({ cellarId, wine }: { cellarId: string; wine: Wine }) {
  const latest = latestRatedTasting(wine.tastings);
  const online = onlineRatingToShow(wine);
  const place = [countryLabel(wine.pays), wine.region ? regionLabel(wine.region) : undefined]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className={`wine-card stock-card wine-card--${wine.typeDeVin}`}>
      <div className="stock-card__row">
        <h2 className="stock-card__name">
          <Link to={`/c/${cellarId}/vin/${wine.id}`}>
            {wine.domaine} {wine.annee}
          </Link>
        </h2>
        {latest?.rating !== undefined ? (
          <span
            className="stock-card__figure wine-card__rating"
            role="img"
            aria-label={tf('rating.cardA11y', { rating: String(latest.rating) })}
          >
            {latest.rating}
          </span>
        ) : online ? (
          <span className="stock-card__figure wine-card__rating">
            <OnlineRatingValue saved={online} compact />
          </span>
        ) : (
          // No rating yet: a dash, not an empty corner (owner, 2026-10-15).
          <span
            className="stock-card__figure wine-card__rating stock-card__unrated"
            role="img"
            aria-label={t('rating.none')}
          >
            –
          </span>
        )}
      </div>
      <p className="stock-card__place">{place}</p>
      <div className="stock-card__row stock-card__row--bottom">
        <p className="stock-card__variety">
          {wine.cepage}
          {wine.parcelle ? ` — ${wine.parcelle}` : ''}
        </p>
        <span className="stock-card__figure stock-card__stock">{bottleCount(wineStock(wine))}</span>
      </div>
    </article>
  );
}
